import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useActions } from '../../../hooks/useActions';
import { useNavigateSearch } from '../../../hooks/useNavigateSearch';

import { OrganizationType } from '../../../models/enums/OrganizationType';
import {
  ProductSubscriptionOverviewPageParams,
  RoutePath,
} from '../../../models/enums/RoutePath';
import { EditProductSubscriptionPageParamsType } from '../../../models/types/RoutePath';
import { clearOrganizationContracts } from '../../../store/redux-slices/contracts';
import {
  organizationNameSelector,
  organizationTypeSelector,
} from '../../../store/redux-slices/organizations/selectors';
import { clearSubscriptionHelperData } from '../../../store/redux-slices/subscriptions/common';
import {
  clearSRCurrentSubscriptionEditing,
  clearSRSubscriptionHelperData,
  clearSRSubscriptionOverview,
} from '../../../store/redux-slices/subscriptions/securityResponse';
import { srSubscriptionOverviewSelector } from '../../../store/redux-slices/subscriptions/securityResponse/selectors';
import { loadOrganizationContracts } from '../../../store/saga-slices/contracts';
import { ILoadOrganizationContractsPayload } from '../../../store/saga-slices/contracts/models';
import { loadOrganization } from '../../../store/saga-slices/organizations';
import {
  ILoadEditingAASubscriptionPayload,
  ILoadSubscriptionOverviewPayload,
} from '../../../store/saga-slices/subscriptions/airAmbulance/models';
import { ILoadDatesForNewSubscriptionPayload } from '../../../store/saga-slices/subscriptions/common/models';
import {
  loadDatesForNewSRSubscription,
  loadEditingSRSubscription,
  loadSRSubscriptionOverview,
} from '../../../store/saga-slices/subscriptions/securityResponse';

export const useEditSRSubscriptionPage = () => {
  const navigate = useNavigateSearch();

  const LoadOrganizationContracts = useActions(loadOrganizationContracts);
  const LoadDatesForNewSubscription = useActions(loadDatesForNewSRSubscription);
  const LoadSubscriptionOverview = useActions(loadSRSubscriptionOverview);
  const ClearOrganizationContracts = useActions(clearOrganizationContracts);
  const LoadOrganization = useActions(loadOrganization);
  const LoadEditingSubscription = useActions(loadEditingSRSubscription);
  const ClearSRSubscriptionHelperData = useActions(
    clearSRSubscriptionHelperData,
  );
  const ClearSubscriptionHelperData = useActions(clearSubscriptionHelperData);
  const ClearSubscriptionOverview = useActions(clearSRSubscriptionOverview);
  const ClearCurrentSubscriptionEditing = useActions(
    clearSRCurrentSubscriptionEditing,
  );

  const organizationName = useSelector(organizationNameSelector);
  const organizationType = useSelector(organizationTypeSelector);
  const subscriptionOverview = useSelector(srSubscriptionOverviewSelector);

  const { organizationId, subscriptionId, subscriptionType } =
    useParams<EditProductSubscriptionPageParamsType>();

  useEffect(() => {
    const payload: ILoadSubscriptionOverviewPayload = {
      organizationId,
      subscriptionId,
    };
    LoadSubscriptionOverview(payload);
  }, [LoadSubscriptionOverview, organizationId, subscriptionId]);

  useEffect(() => {
    if (subscriptionOverview && !subscriptionOverview.isDraft) {
      navigate(
        RoutePath.ClientOverviewProductSubscriptionOverviewPageFullPath,
        {
          params: {
            [ProductSubscriptionOverviewPageParams.OrganizationId]:
              organizationId,
            [ProductSubscriptionOverviewPageParams.SubscriptionId]:
              subscriptionId,
            [ProductSubscriptionOverviewPageParams.SubscriptionType]:
              subscriptionType,
          },
        },
      );
    }
  }, [
    navigate,
    organizationId,
    subscriptionId,
    subscriptionOverview,
    subscriptionType,
  ]);

  useEffect(() => {
    const payload: ILoadEditingAASubscriptionPayload = {
      organizationId,
      subscriptionId,
      subscriptionType,
    };

    LoadEditingSubscription(payload);
  }, [
    LoadEditingSubscription,
    organizationId,
    subscriptionId,
    subscriptionType,
  ]);

  const loadDatesForNewSubscriptionHandler = useCallback(
    (value: string, forceLoad = false) => {
      if (!value && !forceLoad) {
        return;
      }

      const loadDatesForNewSubscriptionPayload: ILoadDatesForNewSubscriptionPayload =
        {
          organizationId,
          contractId: value,
        };
      LoadDatesForNewSubscription(loadDatesForNewSubscriptionPayload);
    },
    [LoadDatesForNewSubscription, organizationId],
  );

  useEffect(() => {
    if (
      organizationType === OrganizationType.Agent ||
      organizationType === OrganizationType.Group
    ) {
      navigate(RoutePath.ClientOverviewGeneralPageFullPath, {
        params: { id: organizationId },
      });
    }
  }, [navigate, organizationId, organizationType]);

  useEffect(() => {
    const loadOrganizationContractsPayload: ILoadOrganizationContractsPayload =
      {
        organizationId,
      };

    LoadOrganizationContracts(loadOrganizationContractsPayload);
    LoadOrganization(organizationId);
  }, [
    LoadDatesForNewSubscription,
    LoadOrganization,
    LoadOrganizationContracts,
    organizationId,
  ]);

  useEffect(
    () => () => {
      ClearOrganizationContracts();
      ClearSRSubscriptionHelperData();
      ClearSubscriptionOverview();
      ClearCurrentSubscriptionEditing();
      ClearSubscriptionHelperData();
    },
    [
      ClearSRSubscriptionHelperData,
      ClearOrganizationContracts,
      ClearSubscriptionOverview,
      ClearCurrentSubscriptionEditing,
      ClearSubscriptionHelperData,
    ],
  );

  return {
    organizationName,
    organizationId,
    loadDatesForNewSubscriptionHandler,
    subscriptionType,
  };
};
